import { timeAgo } from '../../lib/format';
import { Icon, type IconName } from '../../lib/icons';
import type { CategoryFilter, Notif } from './api';

/** Per-type icons. */
export const NOTIF_ICON: Record<string, IconName> = {
  mention: 'comment',
  assigned: 'assigned',
  unassigned: 'assigned',
  status_change: 'sync',
  priority_change: 'priority',
  due_date_change: 'due',
  comment: 'comment',
  internal_note: 'note',
  reaction: 'add-reaction',
  attachment_added: 'attach',
  reopened: 'reopen',
  closed: 'closed',
  customer_reply: 'customer-reply',
  watch: 'watch',
  sla_warning: 'sla-warning',
  sla_breached: 'alert',
  pr_opened: 'sync',
  pr_updated: 'sync',
  pr_merged: 'done',
  pr_closed: 'circle-x',
  build_passed: 'build-passed',
  build_failed: 'build-failed',
  deployment_started: 'deploy',
  deployment_completed: 'deploy',
  deployment_failed: 'alert',
  agent_started: 'agent',
  agent_created_pr: 'agent',
  agent_completed: 'agent',
  agent_blocked: 'blocked',
  release_gate: 'gate',
  integration_sync_failed: 'warning',
  system_alert: 'announce',
  morning_brief_ready: 'morning-brief',
};

/** Human labels for the type filter dropdown on the full page. */
export const NOTIF_TYPE_LABEL: Record<string, string> = {
  mention: 'Mention',
  assigned: 'Assigned',
  unassigned: 'Unassigned',
  status_change: 'Status change',
  priority_change: 'Priority change',
  due_date_change: 'Due date change',
  comment: 'Comment',
  internal_note: 'Internal note',
  reaction: 'Reaction',
  attachment_added: 'Attachment',
  reopened: 'Reopened',
  closed: 'Closed',
  customer_reply: 'Customer reply',
  watch: 'Watch',
  sla_warning: 'SLA warning',
  sla_breached: 'SLA breach',
  pr_opened: 'PR opened',
  pr_updated: 'PR updated',
  pr_merged: 'PR merged',
  pr_closed: 'PR closed',
  build_passed: 'Build passed',
  build_failed: 'Build failed',
  deployment_started: 'Deploy started',
  deployment_completed: 'Deploy completed',
  deployment_failed: 'Deploy failed',
  agent_started: 'Agent started',
  agent_created_pr: 'Agent PR',
  agent_completed: 'Agent completed',
  agent_blocked: 'Agent blocked',
  release_gate: 'Release gate',
  integration_sync_failed: 'Sync failed',
  system_alert: 'System alert',
  morning_brief_ready: 'Morning Brief',
};

export const PRIORITY_DOT: Record<Notif['priority'], string> = {
  low: 'bg-slate-300',
  medium: 'bg-brand',
  high: 'bg-orange-500',
  critical: 'bg-rose-600',
};

export const PRIORITY_PILL: Record<Notif['priority'], string> = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-sky-100 text-sky-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-rose-100 text-rose-700',
};

export interface TabDef {
  key: string;
  label: string;
  category?: CategoryFilter;
  status?: 'archived';
}

export const DROPDOWN_TABS: TabDef[] = [
  { key: 'ALL', label: 'All', category: 'ALL' },
  { key: 'MENTION', label: 'Mentions', category: 'MENTION' },
  { key: 'ASSIGNED', label: 'Assigned', category: 'ASSIGNED' },
  { key: 'WATCHING', label: 'Watching', category: 'WATCHING' },
];

export const PAGE_TABS: TabDef[] = [
  { key: 'ALL', label: 'All', category: 'ALL' },
  { key: 'MENTION', label: 'Mentions', category: 'MENTION' },
  { key: 'ASSIGNED', label: 'Assigned to Me', category: 'ASSIGNED' },
  { key: 'WATCHING', label: 'Watching', category: 'WATCHING' },
  { key: 'TICKET_UPDATE', label: 'Ticket Updates', category: 'TICKET_UPDATE' },
  { key: 'PR_DEVELOPMENT', label: 'PR / Development', category: 'PR_DEVELOPMENT' },
  { key: 'SYSTEM', label: 'System', category: 'SYSTEM' },
  { key: 'ARCHIVED', label: 'Archived', status: 'archived' },
  { key: 'TEAM', label: 'Team review' },
];

/** Per-tab empty-state copy (spec §23.2). */
export const EMPTY_COPY: Record<string, { title: string; hint: string }> = {
  ALL: { title: "You're all caught up", hint: "You'll see mentions, ticket updates, and system alerts here." },
  MENTION: { title: 'No mentions', hint: 'When someone @mentions you, it will appear here.' },
  ASSIGNED: { title: 'No assigned ticket updates', hint: 'Updates on tickets assigned to you will appear here.' },
  WATCHING: { title: 'Nothing from watched tickets', hint: 'Updates on tickets you watch will appear here.' },
  TICKET_UPDATE: { title: 'No ticket updates', hint: 'Status, priority, comment, and SLA updates land here.' },
  PR_DEVELOPMENT: { title: 'No development activity', hint: 'PR, build, deployment, and agent events land here.' },
  SYSTEM: { title: 'No system notifications', hint: 'Integration and admin alerts will appear here.' },
  ARCHIVED: { title: 'No archived notifications', hint: 'Notifications you archive are kept here.' },
};

export function NotificationIcon({ n }: { n: Notif }) {
  return (
    <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-surface2 text-base shrink-0">
      <Icon name={NOTIF_ICON[n.type] ?? 'bell'} />
      {(n.priority === 'high' || n.priority === 'critical') && (
        <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-surface ${PRIORITY_DOT[n.priority]}`} />
      )}
    </span>
  );
}

interface ItemProps {
  n: Notif;
  variant?: 'dropdown' | 'page';
  selected?: boolean;
  onOpen?: (n: Notif) => void;
  onToggleRead?: (n: Notif) => void;
  onArchive?: (n: Notif) => void;
  onCopyLink?: (n: Notif) => void;
  /** Mark every notification for this notification's ticket as read. */
  onMarkTicketRead?: (n: Notif) => void;
}

/** Shared notification row used by both the dropdown and the full center page. */
export function NotificationItem({ n, variant = 'page', selected, onOpen, onToggleRead, onArchive, onCopyLink, onMarkTicketRead }: ItemProps) {
  const isDropdown = variant === 'dropdown';
  return (
    <div
      className={`group flex gap-3 px-3 py-2.5 border-b border-border transition-colors ${
        n.read ? 'bg-transparent' : 'bg-brand/5'
      } ${selected ? 'ring-1 ring-brand/40' : ''} hover:bg-surface2`}
    >
      <button
        type="button"
        className="flex flex-1 gap-3 text-left min-w-0"
        onClick={() => onOpen?.(n)}
      >
        <NotificationIcon n={n} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {!n.read && <span className="h-2 w-2 rounded-full bg-brand shrink-0" aria-label="unread" />}
            <span className={`truncate text-sm ${n.read ? 'text-ink' : 'font-semibold text-ink'}`}>{n.title}</span>
          </div>
          {n.body && <div className="text-xs text-muted line-clamp-2 mt-0.5">{n.body}</div>}
          <div className="flex items-center gap-2 mt-1 text-[11px] text-muted">
            {n.ticketCode && (
              <span className="min-w-0 truncate">
                <span className="font-mono text-accent">{n.ticketCode}</span>
                {n.ticketTitle && <span className="text-muted"> · {n.ticketTitle}</span>}
              </span>
            )}
            {n.actor?.name && <span>· {n.actor.name}</span>}
            <span>· {timeAgo(n.createdAt)}</span>
            {!isDropdown && n.priority !== 'low' && n.priority !== 'medium' && (
              <span className={`chip ${PRIORITY_PILL[n.priority]}`}>{n.priority}</span>
            )}
          </div>
        </div>
      </button>

      {/* Hover actions */}
      <div className={`flex items-start gap-1 ${isDropdown ? 'opacity-0 group-hover:opacity-100' : ''} transition-opacity`}>
        {onToggleRead && (
          <button
            type="button"
            className="text-muted hover:text-brand text-xs p-1 rounded"
            title={n.read ? 'Mark as unread' : 'Mark as read'}
            onClick={() => onToggleRead(n)}
          >
            <Icon name={n.read ? 'dot-empty' : 'dot-filled'} />
          </button>
        )}
        {onMarkTicketRead && n.ticketId && (
          <button
            type="button"
            className="text-muted hover:text-brand text-xs p-1 rounded"
            title={`Mark all notifications for ${n.ticketCode ?? 'this ticket'} as read`}
            onClick={() => onMarkTicketRead(n)}
          >
            <Icon name="check-all" />
          </button>
        )}
        {!isDropdown && onCopyLink && n.ticketCode && (
          <button type="button" className="text-muted hover:text-ink text-xs p-1 rounded" title="Copy ticket link" onClick={() => onCopyLink(n)}>
            <Icon name="link" />
          </button>
        )}
        {onArchive && !n.archived && (
          <button type="button" className="text-muted hover:text-ink text-xs p-1 rounded" title="Archive" onClick={() => onArchive(n)}>
            <Icon name="archive" />
          </button>
        )}
      </div>
    </div>
  );
}
